import { useNavigate } from 'react-router-dom';
import { type ColumnDef } from '@tanstack/react-table';
import { LoadingSpinner, Table, TextLink } from '@shared/components';
import { usePermissions } from '@/features/auth';
import { EyeIcon } from 'lucide-react';
import { RoleFlags } from './RoleFlags';
import type { RoleSummary } from '../types/roles.types';

interface Props {
  data: RoleSummary[];
  isLoading?: boolean;
  emptyMessage?: string;
}

export function RolesDataTable({ data, isLoading = false, emptyMessage }: Props) {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const canRead = hasPermission({ resource: 'role', action: 'read' });

  if (isLoading) {
    return <LoadingSpinner size="xl" className="py-12" message="Loading roles..." />;
  }

  const columns: ColumnDef<RoleSummary>[] = [
    {
      id: 'display_name',
      header: 'Display name',
      cell: (info) => {
        const role = info.row.original;
        return canRead ? (
          <TextLink to={`/admin/roles/${role.id}`} color="primary" className="font-semibold">
            {role.display_name ?? role.name}
          </TextLink>
        ) : (
          <span className="text-sm font-semibold">{role.display_name ?? role.name}</span>
        );
      },
    },
    {
      accessorKey: 'name',
      header: 'Name',
      cell: (info) => <span className="font-mono text-sm">{info.getValue() as string}</span>,
    },
    {
      accessorKey: 'description',
      header: 'Description',
      cell: (info) => {
        const value = info.getValue() as string | null;
        return <span className="text-sm">{value ?? '—'}</span>;
      },
    },
    {
      accessorKey: 'permissions_count',
      header: 'Permissions',
      cell: (info) => <span className="text-sm">{info.getValue() as number}</span>,
    },
    {
      accessorKey: 'users_count',
      header: 'Users',
      cell: (info) => <span className="text-sm">{info.getValue() as number}</span>,
    },
    {
      id: 'flags',
      header: 'Flags',
      cell: (info) => {
        const role = info.row.original;
        return (
          <div className="flex gap-1">
            <RoleFlags is_elevated={role.is_elevated} is_internal={role.is_internal} />
          </div>
        );
      },
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: (info) => {
        if (!canRead) {
          return null;
        }
        const role = info.row.original;
        return (
          <TextLink
            onClick={() => navigate(`/admin/roles/${role.id}`)}
            color="primary"
            startIcon={<EyeIcon size={20} />}
            ariaLabel={`View role ${role.name}`}
          />
        );
      },
    },
  ];

  return (
    <Table<RoleSummary>
      data={data}
      columns={columns}
      enableSorting={false}
      emptyMessage={emptyMessage}
    />
  );
}
